// Where screenshots of a demo wall on the brand's real homepage are uploaded (D-076). Made for
// Claude in Chrome: plain file fields it can put a screenshot in, one per size, for the
// homepage and for a product page (D-088), and the finished pitch video (D-106). People can use
// it too.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, del, post } from "../api";

interface Demos {
  demos: {
    id: string;
    name: string;
    link: string;
    shots: Record<(typeof KINDS)[number]["kind"], boolean>;
    /** The pitch video (D-106), when there is one. */
    video: string | null;
  }[];
}

const KINDS = [
  { kind: "desktop", label: "Computer" },
  { kind: "phone", label: "Phone" },
  { kind: "product-desktop", label: "Product page computer" },
  { kind: "product-phone", label: "Product page phone" },
] as const;

function read(file: File) {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function AdminDemoShots() {
  const { id } = useParams();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["demos"], queryFn: () => api<Demos>("/v1/admin/demos") });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const d = list.data?.demos.find((x) => x.id === id);
  if (list.error)
    return (
      <div className="onb">
        {list.error instanceof ApiError && list.error.status === 404
          ? "Page not found"
          : list.error.message}
      </div>
    );
  if (!list.data) return <div className="onb muted">…</div>;
  if (!d) return <div className="onb">That demo wasn't found.</div>;
  const shotUrl = (kind: string) =>
    `${d.link.replace("/demo/", "/api/v1/public/demos/")}/shots/${kind}?t=${Date.now()}`;

  const upload = async (kind: string, file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(kind);
    try {
      const r = await post<{ summary: string }>(`/v1/admin/demos/${d.id}/shots/${kind}`, {
        image: await read(file),
      });
      setMsg((m) => ({ ...m, [kind]: r.summary }));
      qc.invalidateQueries({ queryKey: ["demos"] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The upload didn't work.");
    } finally {
      setBusy(null);
    }
  };

  /** The video goes as its own bytes, not inside JSON: it can be 100 MB. */
  const uploadVideo = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy("video");
    try {
      const res = await fetch(`/api/v1/admin/demos/${d.id}/video`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new ApiError(res.status, body?.message ?? "The upload didn't work.", "");
      setMsg((m) => ({ ...m, video: body.summary }));
      qc.invalidateQueries({ queryKey: ["demos"] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The upload didn't work.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="content" style={{ margin: "0 auto" }}>
      <div className="stack" style={{ gap: 18 }}>
        <img src="/logo.svg" alt="Brandwall" style={{ height: 26, alignSelf: "start" }} />
        <h1>Screenshots for {d.name}</h1>
        <p className="muted">
          Screenshots of the demo wall on {d.name}'s own homepage and on one of its product pages.
          They show on the demo page and in the report. A new screenshot replaces the old one.
        </p>
        {error && <div className="error">{error}</div>}
        <div className="grid2" style={{ gap: 14 }}>
          {KINDS.map(({ kind, label }) => (
            <section key={kind} className="card stack">
              <label className="field" htmlFor={`shot-${kind}`}>
                {label}
                <input
                  id={`shot-${kind}`}
                  name={`shot-${kind}`}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={`${label} screenshot`}
                  disabled={busy !== null}
                  onChange={(e) => upload(kind, e.target.files?.[0])}
                />
              </label>
              {busy === kind && <p className="small muted">Uploading…</p>}
              {msg[kind] && <p className="small">{msg[kind]}</p>}
              {d.shots[kind] && (
                <>
                  <img
                    src={shotUrl(kind)}
                    alt={`${label} screenshot`}
                    style={{ width: "100%", borderRadius: 10, border: "1px solid var(--line)" }}
                  />
                  <button
                    type="button"
                    className="btn link small"
                    style={{ justifySelf: "start" }}
                    onClick={async () => {
                      await del(`/v1/admin/demos/${d.id}/shots/${kind}`);
                      setMsg((m) => ({ ...m, [kind]: "" }));
                      qc.invalidateQueries({ queryKey: ["demos"] });
                    }}
                  >
                    Remove
                  </button>
                </>
              )}
            </section>
          ))}
        </div>
        <section className="card stack">
          <label className="field" htmlFor="pitch-video">
            Pitch video
            <input
              id="pitch-video"
              name="pitch-video"
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              aria-label="Pitch video"
              disabled={busy !== null}
              onChange={(e) => uploadVideo(e.target.files?.[0])}
            />
          </label>
          <p className="small muted">
            The finished pitch video, MP4 or WebM. It plays on the landing page of every recipient
            linked to this demo, and in the report.
          </p>
          {busy === "video" && <p className="small muted">Uploading…</p>}
          {msg.video && <p className="small">{msg.video}</p>}
          {d.video && (
            <>
              {/* biome-ignore lint/a11y/useMediaCaption: a narrated pitch without a captions file */}
              <video
                src={d.video}
                controls
                playsInline
                preload="metadata"
                style={{ width: "100%", borderRadius: 10, border: "1px solid var(--line)" }}
              />
              <button
                type="button"
                className="btn link small"
                style={{ justifySelf: "start" }}
                onClick={async () => {
                  await del(`/v1/admin/demos/${d.id}/video`);
                  setMsg((m) => ({ ...m, video: "" }));
                  qc.invalidateQueries({ queryKey: ["demos"] });
                }}
              >
                Remove
              </button>
            </>
          )}
        </section>
        <a
          className="btn secondary"
          href={d.link}
          target="_blank"
          rel="noreferrer"
          style={{ alignSelf: "start" }}
        >
          Open the demo
        </a>
      </div>
    </div>
  );
}
